import { useState, useEffect, useRef } from 'react';
import {
  Popover,
  PopoverHeader,
  PopoverBody
} from 'reactstrap'
import classnames from 'classnames'

import { tr } from 'react-hook-form-auto'

import { BuyForm } from './BuyForm'
import { BuyableSearchForm } from './BuyableSearchForm'
import { IngredientAmounts } from '../ingredients/IngredientAmounts'
import { ListTransition } from '../../ui/transitions/ListTransition'
import { JustBoughtView } from './JustBoughtView'
import { useShopData } from './shopData'
import { BuyableSearchResult } from './BuyableSearchResult'

import { PlaceChooser } from './PlaceChooser'

import { IngredientGroup, useGroups } from '../../models/ingredientGroup'
import {
  Place,
  usePlaces,
  orderGroups,
  isGroupHidden
} from '../../models/place'

import styles from './ShopView.sass'
import { Button } from '../../ui/bootstrap/Button';
import { Icon } from '../../ui/bootstrap/bootstrap'

const placeStorageKey = 'shop_selected_place'

const formName = 'buy-update'

// Used to filter transKey
const Diver = ({ className, children }) =>
  <div className={className}>
    {children}
  </div>

const GroupDiv = ({ groupClasses, name, children }) =>
  <div
    className={groupClasses}
    key={name}
  >
    {children}
  </div>

export const ShopView = () => {
  const {
    buys,
    postponedBuys,
    doAdd,
    doBuy,
    postpone,
    ingredientUpdate
  } = useShopData()

  const [ editActive, setEditActive ] = useState(false)
  const [ editingBuy, setEditingBuy ] = useState(false)
  const [ showOrigin, setShowOrigin ] = useState('')
  const [ selectedPlaceId, setSelectedPlaceId ] = useState('')
  const [ showUnavailable, setShowUnavailable ] = useState(false)
  const ingredientSearch = useRef(null)

  const [ places ] = usePlaces()
  const [ groups ] = useGroups()
  Place.usePlaceUpdate()
  Place.usePlaceDeleted()

  // Read after mount so SSR markup and hydration match
  useEffect(() => {
    setSelectedPlaceId(localStorage.getItem(placeStorageKey) || '')
  }, [])

  const handlePlaceSelect = (id) => {
    setSelectedPlaceId(id)
    setShowUnavailable(false)
    localStorage.setItem(placeStorageKey, id)
  }

  const selectedPlace = places &&
    places.find(place => place.id == selectedPlaceId) || null

  const buyAvailableHere = (buy) => {
    if (!selectedPlace)
      return true

    const ingredientPlaces = buy.ingredient &&
      buy.ingredient.places || []

    return ingredientPlaces.length == 0 ||
      ingredientPlaces.some(place => place.id == selectedPlace.id)
  }

  const buysByIngredientId = buys && buys.reduce((hash, buy) => {
    const { ingredient: { id } } = buy
    hash[id] = buy

    return hash
  }, {}) || {}

  const editBuy = (buy) => {
    setEditingBuy(buy)

    editToggle()
  }

  const editToggle = () => {
    setEditActive(!editActive)
  }

  const editClose = () => {
    setEditActive(false)
  }

  const renderOrigins = (buy, cssID) => {
    if (buy && buy.id == showOrigin) {
      return (
        <Popover
          placement="bottom"
          isOpen={true}
          toggle={handleShowOrigins}
          target={cssID}
        >
          <PopoverHeader>
            {tr('models.origins')}
          </PopoverHeader>
        </Popover>
      )
    }
  }

  const handleAdd = (doc) => {
    const type = doc.__typename == 'Ingredient' ? 'ingredient' : 'meal'
    const isIngredient = type == 'ingredient'

    const add = id => {
      doAdd({
        type,
        unit: 'u',
        amount: 1,
        ingredientName: isIngredient && doc.name || '',
        id
      })
    }

    if (doc === 'add')
      editToggle()
    else {
      const { id } = doc
      if (id) {
        add(id)
      } else {
        if (isIngredient) {
          ingredientUpdate(doc).then(({ data }) => {
            add(data.ingredientUpdate.id)
          })
        }
      }

      ingredientSearch.current.reset()
    }
  } 

  const handleCreate = (doc) => {
    handleAdd({
      ...doc,
      __typename: 'Ingredient'
    })
  }

  const handleShowOrigins = (buyId, e) => {
    console.log("HANDLE SHOW", buyId)
    setShowOrigin(buyId)
  }

  const renderIngredient = (buy) => {
    const { ingredient } = buy

    const ingredientName = ingredient.name

    const handleEdit = editBuy.bind(null, buy)

    if (ingredient) {
      return (
        <div
          className={styles.ingredient}
        >
          <div
            key="buy"
            className={styles.ingredientBuy}
          >
            <Button
              iconClass="check"
              className={styles.buyButton}
              type="primary"
              outline
              onClick={() => doBuy(buy, selectedPlaceId || null)}
            />
          </div>
          <div
            key="name"
            className={styles.ingredientName}
          >
            {ingredientName}
          </div>
          <IngredientAmounts
            amountClassName={styles.amount}
            className={styles.amounts}
            buy={buy}
          />
          <div className={styles.buttons}>
            <div>
              <Button
                iconClass="bars"
                className={styles.button}
                onClick={handleEdit}
              />
            </div>
          </div>
        </div>
      )
    } else {
      // FIXME this should not happen
      // FIXME hahahaha
      return "---"
    }
  }

  const renderListElement = (buy) => {
    return (
      <Diver 
        key={buy.id}
        transKey={buy.id}
        className={styles.item}
      >
        {renderIngredient(buy)}
      </Diver>
    )
  }

  const renderIngredientList = (buys) => {
    return buys.map(renderListElement)
  }

  const sortGroupNames = (names) => {
    if (!selectedPlace)
      return [ ...names ].sort()

    const ordered = orderGroups(selectedPlace, groups || [])
      .map(group => group.name)
    const position = name => ordered.indexOf(name)

    return [ ...names ].sort((a, b) => {
      const posA = position(a)
      const posB = position(b)

      if (posA == -1 && posB == -1)
        return a.localeCompare(b)
      if (posA == -1)
        return 1
      if (posB == -1)
        return -1
      return posA - posB
    })
  }

  const renderList = (groupData) => {
    if (groupData) {
      const groupNames = sortGroupNames(Object.keys(groupData))

      return groupNames.map(name => {
        const buys = groupData[name]

        if (buys && buys.length > 0) {
          return (
            <GroupDiv
              className={styles.group}
              key={name}
              transKey={name}
            >
              <div className={styles.header}>
                <div className={styles.name}>
                  {name} 
                </div>
              </div>
              <ListTransition
                className={styles.buys}
                styles={styles}
              >
                {renderIngredientList(buys)}
              </ListTransition>
            </GroupDiv>
          )
        } else
          return null
      })
    } else
      return null
  }

  // render()
  const buyHiddenHere = buy => {
    if (!selectedPlace)
      return false

    const group = buy.ingredient && buy.ingredient.group

    return Boolean(group) && isGroupHidden(selectedPlace, group)
  }
  const availableBuys = buys &&
    buys.filter(buy =>
      buyAvailableHere(buy) && !buyHiddenHere(buy)
    ) || []
  const unavailableBuys = buys &&
    buys.filter(buy =>
      (!buyAvailableHere(buy) || buyHiddenHere(buy)) &&
        IngredientGroup.hasAmounts(buy)
    ) || []

  const groupData = buys &&
    IngredientGroup.processGroups(availableBuys)

  const renderUnavailable = () => {
    if (!selectedPlace || unavailableBuys.length == 0)
      return null

    return (
      <div className={styles.unavailable}>
        <div
          className={styles.unavailableHeader}
          onClick={() => setShowUnavailable(!showUnavailable)}
        >
          <Icon icon={showUnavailable ? 'chevron-up' : 'chevron-down'} />
          <span className={styles.unavailableText}>
            {tr('models.place.unavailable', {
              count: unavailableBuys.length,
              name: selectedPlace.name
            })}
          </span>
        </div>
        {
          showUnavailable &&
            <div className={classnames(styles.group, styles.ghosted)}>
              <div className={styles.buys}>
                {unavailableBuys.map(renderListElement)}
              </div>
            </div>
        }
      </div>
    )
  }

  const handlePostpone = (seconds) => {
    postpone(editingBuy.id, seconds)
    editClose()
  }

  return (
    <div key="shop-view" className={styles.shop}>
      {
        editingBuy &&
          <BuyForm
            form={formName}
            onClose={editClose}
            visible={editActive}
            doc={editingBuy}
            ingredient={editingBuy && editingBuy.ingredient}
            initialGroupId={editingBuy && editingBuy.groupId}
            onPostpone={handlePostpone}
          /> 
      }
      <BuyableSearchForm
        onSelect={handleAdd}
        onCreate={handleCreate}
        buysByIngredientId={buysByIngredientId}
        buyableRenderer={BuyableSearchResult}
        ref={element => { ingredientSearch.current = element }}
        autoFocus
      />
      <PlaceChooser
        places={places}
        selectedId={selectedPlaceId}
        onSelect={handlePlaceSelect}
      />
      <ListTransition className={styles.buys} styles={styles}>
        {renderList(groupData)}
      </ListTransition>
      {renderUnavailable()}
      <JustBoughtView />
    </div>
  )
}
